import { useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/config";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCategories = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/categories/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching categories:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!name.trim()) {
      setFormError("Category name is required.");
      return;
    }

    fetch(`${API_BASE_URL}/api/categories/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ name: name.trim() }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to create category.";
          if (data && data.name && Array.isArray(data.name)) {
            msg = data.name[0];
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then(() => {
        setFormSuccess("Category created.");
        setName("");
        fetchCategories();
      })
      .catch((err) => setFormError(err.message || "Failed to create category."));
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditingName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = (e) => {
    e.preventDefault();
    if (!editingName.trim()) return;

    fetch(`${API_BASE_URL}/api/categories/${editingId}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ name: editingName.trim() }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to update category.";
          if (data && data.name && Array.isArray(data.name)) {
            msg = data.name[0];
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then((updated) => {
        setCategories((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c))
        );
        cancelEdit();
      })
      .catch((err) => alert(err.message || "Failed to update category."));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this category?")) return;
    setDeleteLoading(true);

    fetch(`${API_BASE_URL}/api/categories/${id}/`, {
      method: "DELETE",
      headers: authHeaders(),
    })
      .then((res) => {
        if (res.status !== 204 && !res.ok) {
          throw new Error("Failed to delete category.");
        }
        setCategories((prev) => prev.filter((c) => c.id !== id));
      })
      .catch((err) => alert(err.message || "Failed to delete category."))
      .finally(() => setDeleteLoading(false));
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Categories</h1>

      <div className="glass-card p-4 mb-4">
        <h2 className="text-sm font-semibold mb-3">Add Category</h2>
        <form onSubmit={handleCreate} className="flex gap-2 max-w-md">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
            placeholder="Category name"
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-md bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
          >
            Save
          </button>
        </form>
        {(formError || formSuccess) && (
          <div className="mt-2 text-xs">
            {formError && <div className="text-red-600">{formError}</div>}
            {formSuccess && (
              <div className="text-green-600">{formSuccess}</div>
            )}
          </div>
        )}
      </div>

      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">
                Name
              </th>
              <th className="!text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-4 py-4 text-center text-muted" colSpan={2}>
                  Loading...
                </td>
              </tr>
            )}
            {!loading && categories.length === 0 && (
              <tr>
                <td className="px-4 py-4 text-center text-muted" colSpan={2}>
                  No categories.
                </td>
              </tr>
            )}
            {!loading &&
              categories.map((cat) => (
                <tr key={cat.id} className="border-t">
                  <td className="px-4 py-2">
                    {editingId === cat.id ? (
                      <form onSubmit={saveEdit} className="flex gap-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 rounded-md border border-white/50 dark:border-white/15 px-2 py-1 text-sm"
                        />
                      </form>
                    ) : (
                      cat.name
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {editingId === cat.id ? (
                      <>
                        <button
                          className="text-xs px-2 py-1 mr-2 rounded border border-white/50 dark:border-white/15"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="text-xs px-2 py-1 mr-2 rounded border border-white/50 dark:border-white/15"
                          onClick={() => startEdit(cat)}
                        >
                          Edit
                        </button>
                        <button
                          disabled={deleteLoading}
                          className="text-xs px-2 py-1 rounded bg-red-500 text-white"
                          onClick={() => handleDelete(cat.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Categories;
